import {
  Scene,
  Camera,
  Mesh,
  RawShaderMaterial,
  WebGLRenderTarget,
  PlaneBufferGeometry,
  Vector2,
  LinearFilter,
  RGBFormat,
  CustomBlending,
  AddEquation,
  OneFactor,
  Color
} from 'three'

import { Reflector } from 'three/examples/jsm/objects/Reflector'
import vertexShader from '@/webGL/shaders/blurred-reflector.vert'
import fragmentShader from '@/webGL/shaders/blurred-reflector.frag'

const iterations = 8
const size = 1024

import * as THREE from 'three'

export class BlurredReflector extends Reflector {
  constructor ({
    geometry = new PlaneBufferGeometry(10, 5),
    blurSize = .25,
    beforeRender = () => {},
    afterRender = () => {}
  } = {}) {
    super(geometry, {
      textureWidth: size,
      textureHeight: size,
      shader: {
        ...Reflector.ReflectorShader,
        fragmentShader: `
          uniform vec3 color;
          uniform sampler2D tDiffuse;
          varying vec4 vUv;

          float blendOverlay( float base, float blend ) {
            return( base < 0.5 ? ( 2.0 * base * blend ) : ( 1.0 - 2.0 * ( 1.0 - base ) * ( 1.0 - blend ) ) );
          }

          vec3 blendOverlay( vec3 base, vec3 blend ) {
            return vec3( blendOverlay( base.r, blend.r ), blendOverlay( base.g, blend.g ), blendOverlay( base.b, blend.b ) );
          }

          void main() {
            vec4 base = texture2DProj( tDiffuse, vUv );
            gl_FragColor = vec4( blendOverlay( base.rgb, color ), (base.r + base.g + base.b) * (base.r + base.g + base.b) / 9. );
          }
        `
      }
    })

    const renderTargetParameters = {
      minFilter: LinearFilter,
      magFilter: LinearFilter,
      format: RGBFormat,
      stencilBuffer: false
    }

    this.blurSize = blurSize

    this.blurRenderTargetA = new WebGLRenderTarget(size, size, renderTargetParameters)
    this.blurRenderTargetB = new WebGLRenderTarget(size, size, renderTargetParameters)
    this.blurRenderTarget = iterations % 2
      ? this.blurRenderTargetA
      : this.blurRenderTargetB

    this.material.uniforms.tDiffuse.value = this.blurRenderTarget.texture
    this.material.uniforms.color.value.set(0xaaaaaa)
    this.material.transparent = true

    this.blurPlane = new Mesh(
      new PlaneBufferGeometry(2, 2),
      new RawShaderMaterial({
        vertexShader,
        fragmentShader,
        transparent: true,
        uniforms: {
          iChannel0: { value: null },
          iResolution: { value: new Vector2(size, size) },
          flip: { value: false },
          direction: { value: new Vector2() }
        }
      })
    )

    // this.getRenderTarget().texture.format = RGBAFormat

    this.blurScene = new Scene().add(this.blurPlane)
    this.blurCamera = new Camera()

    const onBeforeRender = this.onBeforeRender
    const black = new Color(0x000000)

    this.onBeforeRender = (renderer, scene, camera, ...args) => {
      beforeRender(renderer, camera, ...args)

      const currentClearColor = renderer.getClearColor()
      const currentClearAlpha = renderer.getClearAlpha()
      const background = scene.background

      renderer.setClearColor(0x000000, 1)
      scene.background = black

      onBeforeRender(renderer, scene, camera, ...args)
      this.blur(renderer, camera)

      scene.background = background
      renderer.setClearColor(currentClearColor, currentClearAlpha)

      afterRender(renderer, scene, camera, ...args)
    }
  }

  blur (renderer, camera) {
    let readBuffer = this.blurRenderTargetA
    let writeBuffer = this.blurRenderTargetB

    const currentRenderTarget = renderer.getRenderTarget()
    const currentXrEnabled = renderer.xr.enabled
    const currentShadowAutoUpdate = renderer.shadowMap.autoUpdate
    renderer.xr.enabled = false
    renderer.shadowMap.autoUpdate = false

    for (var i = 0; i < iterations; i++) {
      // var radius = (iterations - i - 1) * 0.25
      var radius = (iterations - i - 1) * this.blurSize

      this.blurPlane.material.uniforms.direction.value.set(
        (i + 1) % 2 * radius,
        i % 2 * radius
      )

      this.blurPlane.material.uniforms.iChannel0.value = i === 0
        ? this.getRenderTarget().texture
        : readBuffer.texture

      renderer.setRenderTarget(writeBuffer)
      renderer.clear()
      renderer.render(this.blurScene, this.blurCamera)

      let t = writeBuffer
      writeBuffer = readBuffer
      readBuffer = t
    }

    renderer.xr.enabled = currentXrEnabled
    renderer.shadowMap.autoUpdate = currentShadowAutoUpdate
    renderer.setRenderTarget(currentRenderTarget)

    let viewport = camera.viewport
    viewport !== undefined && renderer.state.viewport(viewport)
  }
}
